import React, { useState } from "react";
import { formatTimestamp } from "../../components/Shared/SharedMethods";
import { useDispatch } from "react-redux";
import {
  getInsuranceInvoiceDetail,
  getInvoiceDetail,
  printInvoice,
} from "../../store/slices/InvoicesSlice";
import { useNavigate } from "react-router-dom";
import CustomPagination from "../../components/Shared/CustomPagination";
import { useTranslation } from "react-i18next";
import { formatDate } from "../Transactions/TransactionsTable";
import ReceivePaymentInvoice from "./ReceivePaymentInvoice";
import TableLoading from "../../components/Shared/TableLoading";
import CustomTable from "../../components/Shared/CustomTable";
import { CancelIcon, PaymentIcon, PrintIcon } from "../../components/Shared/TableActionIcons";

export default function InvoiceTable({
  invoicesList,
  loading,
  onPageChange
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [receivePaymentDialogVisible, setReceivePaymentDialogVisible] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const handlePrintInvoice = async (id) => {
    try {
      const resultAction = await dispatch(printInvoice(id));
      if (printInvoice.fulfilled.match(resultAction)) {
        navigate("/manager/show-invoice");
      } else if (printInvoice.rejected.match(resultAction)) {
        console.error("Failed to print invoice:", resultAction.error);
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  const onPageChangeEvent = async (newPage, newRowsPerPage) => {
    if (onPageChange) {
      onPageChange(newPage, newRowsPerPage);
    }
  };

  const templateBody = (rowData) => (
    <div className="flex gap-1">
      <PrintIcon
        onClick={() => {
          handlePrintInvoice(rowData?.id);
        }}
        title={t("print")}
      />
      {rowData.status !== "Paid" && rowData?.status !== "cancel" && (
        <PaymentIcon
          onClick={() => {
            navigate("/manager/invoices/payment", {
              state: {
                selectedEntries: [rowData],
              },
            });
          }}
          title={t("payment")}
        />
      )}
      {rowData?.status !== "cancel" && (
        <CancelIcon
          onClick={() => {
            if (rowData?.invoice_type === "credit") {
              dispatch(
                getInsuranceInvoiceDetail({
                  invoice_id: rowData?.company_invoice_id,
                }),
              ).then(() => {
                navigate("/manager/insuranceInvoice/create", {
                  state: { data: rowData, cancel: true },
                });
              });
            } else {
              dispatch(
                getInvoiceDetail({
                  invoice_id: rowData?.id,
                }),
              ).then(() => {
                navigate("/manager/invoices/create", {
                  state: { data: rowData, cancel: true },
                });
              });
            }
          }}
          title={t("cancel")}
        />
      )}
    </div>
  );

  const statusBody = (data) => {
    const statusClasses = "p-1 !w-[60px] text-center text-white rounded-md";
    if (data?.status === "Paid") {
      return <div className={`bg-green-500 ${statusClasses}`}>{t("Paid")}</div>;
    } else if (data?.status === "Unpaid" || !data?.status) {
      return <div className={`bg-red-500 ${statusClasses}`}>{t("Unpaid")}</div>;
    } else if (data?.status === "cancel") {
      return <div className={`bg-red-500 ${statusClasses}`}>{t("Cancel")}</div>;
    }
    return <div className={`bg-red-500 ${statusClasses}`}>{t("Unpaid")}</div>;
  };

  const sequenceBodyTemplate = (rowData, options) => {
    return (
      options.rowIndex + 1 + ((invoicesList?.page || 1) - 1) * (invoicesList?.per_page || 10)
    );
  };

  const balanceBody = (data, column) => {
    const fieldName = column?.field;
    if (fieldName === "total_company_amount")
      return <>{(data?.company_share || 0) + (data?.company_tax || 0)} {t("SAR")}</>;
    return <>{data[fieldName]} {t("SAR")}</>;
  };

  const columnsData = [
    { body: templateBody, header: t("actions") },
    { body: sequenceBodyTemplate, header: t("no"), style: { width: "3rem" } },
    { field: "date", header: t("date"), body: (rowData) => formatDate(rowData.date) },
    { field: "invoice_number", header: t("invoiceNumber") },
    { field: "file_number", header: t("fileNumber") },
    { field: "customer_name", header: `${t("Customer")} ${t("name")}` },
    { field: "patient_share", header: `${t("Customer")} ${t("amount")}`, body: balanceBody },
    { field: "discount", header: t("discount") },
    { field: "patient_tax", header: `${t("Customer")} ${t("vat")}`, body: balanceBody },
    { field: "amount", header: t("total_customer_amount"), body: balanceBody },
    { field: "company_share", header: t("company_amount"), body: balanceBody },
    { field: "company_tax", header: t("company_vat"), body: balanceBody },
    { field: "total_company_amount", header: t("total_company_amount"), body: balanceBody },
    { field: "payment_method", header: t("payment_method") },
    { field: "nationality", header: t("nationality") },
    { body: statusBody, field: "paid", header: t("status") },
    { field: "description", header: t("description") },
  ];

  return (
    <>
      <CustomTable
        data={invoicesList?.data}
        isLoading={loading}
        loadingIcon={<TableLoading />}
        columns={columnsData}
        rows={invoicesList?.per_page}
        rootyStyles="max-h-[50vh] overflow-auto"
      />
      {invoicesList?.data?.length > 0 && (
        <CustomPagination
          currentPage={invoicesList?.page}
          rowsPerPage={invoicesList?.per_page}
          totalRecords={invoicesList?.total_pages}
          totalRecord={invoicesList?.total_record}
          onPageChange={onPageChangeEvent}
        />
      )}
      <ReceivePaymentInvoice
        open={receivePaymentDialogVisible}
        setOpen={setReceivePaymentDialogVisible}
        selectedEntries={selectedEntry ? [selectedEntry] : []}
      />
    </>
  );
}
